import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  getQuizDetails,
  getCurrentQuestion,
  getAllQuestionsBySection,
  getOngoingSectionDetails
} from '@selectors/quizTaking';
import { setCurrentQuestionInfo } from '@actions/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import useOngoingSection from './useOngoingSection';
import { getSectionInfo } from '../../utils';

const useQuestionPagination = () => {
  const dispatch = useDispatch();

  const { query } = useRouter();
  const { quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const currentQuestion = useSelectorWithShallowEqual(getCurrentQuestion);

  const questionsBySection = useSelectorWithShallowEqual(
    getAllQuestionsBySection
  );

  const { hasOngoingSectional, onGoingSectionUID } = useOngoingSection();

  const ongoingSectionDetails = useSelectorWithShallowEqual(
    getOngoingSectionDetails
  );

  const currentSectionInfo = useMemo(
    () => getSectionInfo(quizDetails.sections, currentQuestion?.question_rank),
    [currentQuestion.question_rank, quizDetails.sections]
  );

  const currentSection = quizDetails.sections[currentSectionInfo.sectionIndex];

  const isCurrentSectionLastQuestion =
    currentQuestion?.question_rank ===
    currentSectionInfo?.startingRank + currentSection?.questions_count - 1;

  const hasPrevious = currentQuestion.question_rank !== 1;

  const hasNext = isCurrentSectionLastQuestion
    ? !!quizDetails.sections[currentSectionInfo.sectionIndex + 1]
    : true;

  const handleOnPrevious = () => {
    if (currentQuestion.question_rank - currentSectionInfo.startingRank > 0) {
      const previousQuestionIndex =
        currentQuestion.question_rank - currentSectionInfo.startingRank - 1;
      const previousQuestionUID =
        questionsBySection[currentSection.uid].allIds[previousQuestionIndex];

      dispatch(
        setCurrentQuestionInfo({
          sectionUID: currentSection.uid,
          questionUID: previousQuestionUID
        })
      );
    } else {
      const previousSection =
        quizDetails.sections[currentSectionInfo.sectionIndex - 1];

      if (
        !previousSection ||
        (hasOngoingSectional && previousSection.uid !== onGoingSectionUID)
      ) {
        return;
      }

      const previousQuestionUID =
        questionsBySection[previousSection.uid].allIds[
          previousSection.questions_count - 1
        ];

      dispatch(
        setCurrentQuestionInfo({
          sectionUID: previousSection.uid,
          questionUID: previousQuestionUID
        })
      );
    }
  };

  const handleOnNext = () => {
    const isCurrentSectionInCompleteQuestion =
      currentQuestion.question_rank <
      currentSectionInfo.startingRank + currentSection.questions_count - 1;

    if (isCurrentSectionInCompleteQuestion) {
      const nextQuestionIndex =
        currentQuestion.question_rank - currentSectionInfo.startingRank + 1;
      const nextQuestionUID =
        questionsBySection[currentSection.uid].allIds[nextQuestionIndex];

      dispatch(
        setCurrentQuestionInfo({
          sectionUID: currentSection.uid,
          questionUID: nextQuestionUID
        })
      );
    } else {
      const nextSection =
        quizDetails.sections[currentSectionInfo.sectionIndex + 1];

      if (
        !nextSection ||
        (hasOngoingSectional && nextSection.uid !== onGoingSectionUID)
      ) {
        const firstSectionUID = quizDetails.sections[0].uid;
        const firstQuestionUID = questionsBySection[firstSectionUID].allIds[0];

        const isLocked =
          hasOngoingSectional &&
          firstSectionUID !== ongoingSectionDetails.section.uid;

        if (isLocked) {
          const firstQuestionUIDOfCurrentSection =
            questionsBySection[onGoingSectionUID].allIds[0];

          return dispatch(
            setCurrentQuestionInfo({
              sectionUID: onGoingSectionUID,
              questionUID: firstQuestionUIDOfCurrentSection
            })
          );
        }

        return dispatch(
          setCurrentQuestionInfo({
            sectionUID: firstSectionUID,
            questionUID: firstQuestionUID
          })
        );
      }

      const nextQuestionUID = questionsBySection[nextSection.uid].allIds[0];

      dispatch(
        setCurrentQuestionInfo({
          sectionUID: nextSection.uid,
          questionUID: nextQuestionUID
        })
      );
    }
  };

  return { hasPrevious, hasNext, handleOnPrevious, handleOnNext };
};

export default useQuestionPagination;
